// タイムキーパー機能 (spec §4.3, M8)。
// セクション開始 → 経過時間 + 完了率 + 終了ボタン。リアルタイム表示は 1s ごとに client-side で
// elapsed を再計算。完了率はサーバの /state ポーリングに依存するため、簡略化のため
// 5s ごとに /state を取得 (HostPanel の polling と分離して TimeKeeper 単体で完結する)。

import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { t, type Locale, type MessageKey } from '@solo-webinar-copilot/i18n';
import type {
  SectionMarker,
  SectionMarkerType,
} from '@solo-webinar-copilot/shared';
import {
  endSectionMarker,
  fetchSectionMarkers,
  fetchState,
  startSectionMarker,
  type SessionState,
} from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

const POLL_MS = 5000;

const TYPES: readonly SectionMarkerType[] = [
  'lecture',
  'exercise',
  'qa',
  'break',
  'other',
];

const TYPE_LABEL_KEY: Record<SectionMarkerType, MessageKey> = {
  lecture: 'in_client.host.timekeeper.type.lecture',
  exercise: 'in_client.host.timekeeper.type.exercise',
  qa: 'in_client.host.timekeeper.type.qa',
  break: 'in_client.host.timekeeper.type.break',
  other: 'in_client.host.timekeeper.type.other',
};

export interface TimeKeeperProps {
  ctx: AppContext;
  locale: Locale;
}

export function TimeKeeper({ ctx, locale }: TimeKeeperProps): ReactElement {
  const [active, setActive] = useState<SectionMarker | null>(null);
  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(Date.now());

  const refresh = useCallback(async () => {
    const [m, s] = await Promise.all([
      fetchSectionMarkers(ctx),
      fetchState(ctx).catch(() => null),
    ]);
    if (m.ok) {
      setActive(m.active);
      setError(null);
    } else {
      setError(m.error);
    }
    if (s) setState(s);
  }, [ctx]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (cancelled) return;
      await refresh();
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [refresh]);

  // 1s ごとに表示更新
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleStart = async (draft: {
    name: string;
    type: SectionMarkerType;
    plannedDurationSeconds?: number;
  }) => {
    const r = await startSectionMarker(ctx, draft);
    if (r.ok) {
      setActive(r.item);
      setError(null);
      // 直後に state を再取得 (リセットされた進捗を即反映)
      try {
        setState(await fetchState(ctx));
      } catch {
        // ignore
      }
    } else {
      setError(r.error);
    }
  };

  const handleEnd = async () => {
    if (!active) return;
    const r = await endSectionMarker(ctx, active.id);
    if (r.ok) {
      setActive(null);
      setError(null);
    } else {
      setError(r.error);
    }
  };

  return (
    <section data-testid="timekeeper">
      <h2>{t('in_client.host.timekeeper.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="timekeeper-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      {active ? (
        <ActiveView
          locale={locale}
          marker={active}
          state={state}
          now={now}
          onEnd={() => void handleEnd()}
        />
      ) : (
        <>
          <p data-testid="timekeeper-no-active">
            {t('in_client.host.timekeeper.no_active', locale)}
          </p>
          <StartForm locale={locale} onStart={(d) => void handleStart(d)} />
        </>
      )}
    </section>
  );
}

interface ActiveViewProps {
  locale: Locale;
  marker: SectionMarker;
  state: SessionState | null;
  now: number;
  onEnd: () => void;
}

function ActiveView({ locale, marker, state, now, onEnd }: ActiveViewProps): ReactElement {
  const elapsedSec = Math.max(0, Math.floor((now - marker.started_at) / 1000));
  const planned = marker.planned_duration_seconds;
  const remaining = planned !== null ? planned - elapsedSec : null;
  const completion = computeCompletion(state);

  return (
    <div data-testid="timekeeper-active">
      <div>
        <strong data-testid="timekeeper-name">{marker.name}</strong>{' '}
        <small style={{ opacity: 0.7 }}>
          [{t(TYPE_LABEL_KEY[marker.type], locale)}]
        </small>
      </div>
      <div data-testid="timekeeper-elapsed">
        {t('in_client.host.timekeeper.elapsed', locale)}: {formatDuration(elapsedSec)}
      </div>
      {planned !== null && remaining !== null ? (
        remaining >= 0 ? (
          <div data-testid="timekeeper-remaining">
            {t('in_client.host.timekeeper.remaining', locale)}: {formatDuration(remaining)}
          </div>
        ) : (
          <div data-testid="timekeeper-overtime" style={{ color: '#c1121f' }}>
            {t('in_client.host.timekeeper.overtime', locale)}: {formatDuration(-remaining)}
          </div>
        )
      ) : null}
      {completion !== null ? (
        <div data-testid="timekeeper-completion">
          {t('in_client.host.timekeeper.completion', locale)}: {completion.done} /{' '}
          {completion.total} ({Math.round(completion.ratio * 100)}%)
        </div>
      ) : null}
      <button
        type="button"
        onClick={onEnd}
        data-testid="timekeeper-end-button"
      >
        {t('in_client.host.timekeeper.end_section', locale)}
      </button>
    </div>
  );
}

function computeCompletion(
  state: SessionState | null,
): { done: number; total: number; ratio: number } | null {
  if (!state) return null;
  const total = state.participantCount;
  if (total <= 0) return null;
  const done = state.counts.done ?? 0;
  return { done, total, ratio: done / total };
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface StartFormProps {
  locale: Locale;
  onStart: (d: {
    name: string;
    type: SectionMarkerType;
    plannedDurationSeconds?: number;
  }) => void;
}

function StartForm({ locale, onStart }: StartFormProps): ReactElement {
  const [name, setName] = useState('');
  const [type, setType] = useState<SectionMarkerType>('exercise');
  const [plannedMinutes, setPlannedMinutes] = useState('');

  const handle = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    const draft: {
      name: string;
      type: SectionMarkerType;
      plannedDurationSeconds?: number;
    } = { name: name.trim(), type };
    const m = Number.parseInt(plannedMinutes, 10);
    if (Number.isFinite(m) && m > 0) draft.plannedDurationSeconds = m * 60;
    onStart(draft);
    setName('');
    setPlannedMinutes('');
  };

  return (
    <form onSubmit={handle} data-testid="timekeeper-form">
      <div>
        <label>
          {t('in_client.host.timekeeper.name', locale)}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            data-testid="timekeeper-form-name"
            required
          />
        </label>
      </div>
      <div>
        <label>
          {t('in_client.host.timekeeper.type', locale)}
          <select
            value={type}
            onChange={(e) => setType(e.currentTarget.value as SectionMarkerType)}
            data-testid="timekeeper-form-type"
          >
            {TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {t(TYPE_LABEL_KEY[tp], locale)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div>
        <label>
          {t('in_client.host.timekeeper.planned_minutes', locale)}
          <input
            type="number"
            min="0"
            value={plannedMinutes}
            onChange={(e) => setPlannedMinutes(e.currentTarget.value)}
            data-testid="timekeeper-form-planned"
          />
        </label>
      </div>
      <button type="submit" data-testid="timekeeper-form-start">
        {t('in_client.host.timekeeper.start_section', locale)}
      </button>
    </form>
  );
}
